import type { ApolloClient, TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { arcadeOrigin } from '@/config/runtime-config';

/** Papermill jobs as Arcade reports them, and the `/v1` calls that make them. */

export type JobStatus = 'PENDING' | 'DISPATCHED' | 'COMPLETE' | 'FAILED' | 'DELETED';

/** `document`; `audio`, transcribed by Stylus then extracted; or `live`, streamed first. */
export type JobKind = 'document' | 'audio' | 'live';

export interface JobFailure {
  failureClass: string | null;
  failingStep: string | null;
  attemptCount: number | null;
  error: string | null;
}

/** Arcade's stable reason a job produced no extraction; `code` is what to branch on. */
export interface JobError {
  code: string;
  message: string;
}

export interface Job {
  id: string;
  kind?: JobKind | null;
  status: JobStatus;
  phase: string | null;
  credits: number | null;
  error: JobError | null;
  failure: JobFailure | null;
  filename: string | null;
  isSample: boolean | null;
  verdict: string | null;
  pageCount: number | null;
  durationMs?: number | null;
  submittedAt: string | null;
  completedAt: string | null;
  apiKeyId: string | null;
}

export interface JobResult {
  verdict: string | null;
  manifest: Record<string, unknown> | null;
  metrics: Record<string, unknown> | null;
  downloads: {
    extraction: string | null;
    canonicalPdf: string | null;
    transcript?: string | null;
    audio?: string | null;
  };
}

const JOB_FIELDS = gql`
  fragment JobFields on PapermillJob {
    id
    kind
    status
    phase
    credits
    error {
      code
      message
    }
    failure {
      failureClass
      failingStep
      attemptCount
      error
    }
    filename
    isSample
    verdict
    pageCount
    durationMs
    submittedAt
    completedAt
    apiKeyId
  }
`;

export const JOBS: TypedDocumentNode<{
  apiKeys: { id: string; name: string | null; jobs: { edges: { node: Job }[] } }[];
}> = gql`
  query PapermillJobs {
    apiKeys(includeRevoked: true) {
      id
      name
      jobs(first: 50) {
        edges {
          node {
            ...JobFields
          }
        }
      }
    }
  }
  ${JOB_FIELDS}
`;

export const JOB: TypedDocumentNode<{ job: Job & { result: JobResult | null } }, { id: string }> =
  gql`
    query PapermillJob($id: UUID!) {
      job(id: $id) {
        ...JobFields
        result {
          verdict
          manifest
          metrics
          downloads {
            extraction
            canonicalPdf
            transcript
            audio
          }
        }
      }
    }
    ${JOB_FIELDS}
  `;

export const DELETE_JOB: TypedDocumentNode<
  { deleteJob: Pick<Job, 'id' | 'status'> },
  { id: string }
> = gql`
  mutation DeleteJob($id: UUID!) {
    deleteJob(id: $id) {
      id
      status
    }
  }
`;

const JOB_CHECK: TypedDocumentNode<
  { job: Pick<Job, 'id' | 'status' | 'phase' | 'error'> },
  { id: string }
> = gql`
  query PapermillJobCheck($id: UUID!) {
    job(id: $id) {
      id
      status
      phase
      error {
        code
        message
      }
    }
  }
`;

export type Screening =
  { outcome: 'passed' } | { outcome: 'rejected'; error: JobError } | { outcome: 'timeout' };

/** Where a just-submitted job stands with Bindery; null while it is still checking. */
export function screeningOf(job: Pick<Job, 'status' | 'phase' | 'error'>): Screening | null {
  if (job.error) {
    return { outcome: 'rejected', error: job.error };
  }
  if (job.status === 'PENDING' && job.phase === 'STANDARDIZING') {
    return null;
  }
  return { outcome: 'passed' };
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls a new job until Bindery has accepted or rejected the document, or gives up. */
export async function awaitScreening(
  client: ApolloClient,
  id: string,
  { timeoutMs = 90_000, intervalMs = 1_500 } = {},
): Promise<Screening> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data } = await client.query({
      query: JOB_CHECK,
      variables: { id },
      fetchPolicy: 'network-only',
    });
    const screening = data ? screeningOf(data.job) : null;
    if (screening) {
      return screening;
    }
    await sleep(intervalMs);
  }
  return { outcome: 'timeout' };
}

const REJECTION_TITLES: Record<string, string> = {
  DOCUMENT_ENCRYPTED: 'This document is password-protected',
  UNSUPPORTED_FILE_TYPE: 'This file type isn’t supported',
  CONVERSION_FAILED: 'This document couldn’t be opened',
  TOO_MANY_PAGES: 'This document has too many pages',
  DOCUMENT_TOO_LARGE: 'This document is too large',
  UNSUPPORTED_MEDIA: 'This recording couldn’t be decoded',
  NO_AUDIO: 'This file has no audio',
  AUDIO_TOO_LONG: 'This recording is too long',
};

export function rejectionTitle(code: string): string {
  return REJECTION_TITLES[code] ?? 'This document was rejected';
}

/** Every job across the organization's keys, newest first. */
export function allJobs(
  data: { apiKeys: { jobs: { edges: { node: Job }[] } }[] } | undefined,
): Job[] {
  const jobs = (data?.apiKeys ?? []).flatMap((key) => key.jobs.edges.map((edge) => edge.node));
  return jobs
    .filter((job) => job.status !== 'DELETED')
    .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));
}

export function isInFlight(job: Pick<Job, 'status'>): boolean {
  return job.status === 'PENDING' || job.status === 'DISPATCHED';
}

export class V1Error extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'V1Error';
  }
}

async function readError(status: number, text: string): Promise<V1Error> {
  try {
    const body = JSON.parse(text) as { error?: { code?: string; message?: string } };
    return new V1Error(status, body.error?.code, body.error?.message ?? `HTTP ${status}`);
  } catch {
    return new V1Error(status, undefined, `HTTP ${status}`);
  }
}

export interface Sample {
  key: string;
  title: string;
  content_type: string;
}

/** A recording, uploaded or streamed live: it ends in a transcript either way. */
export function isAudioJob(job: Pick<Job, 'kind'>): boolean {
  return job.kind === 'audio' || job.kind === 'live';
}

/** Still being streamed to: a live session that has not ended. */
export function isStreaming(job: Pick<Job, 'kind' | 'phase' | 'status'>): boolean {
  return (
    job.kind === 'live' &&
    job.status === 'PENDING' &&
    ['CONNECTING', 'READY', 'STREAMING'].includes(job.phase ?? 'CONNECTING')
  );
}

/** What to call a job: its filename, or what it is when it has none worth showing. */
export function jobTitle(job: Pick<Job, 'kind' | 'filename'>): string {
  if (job.kind === 'live') {
    return 'Live session';
  }
  return job.filename || (job.kind === 'audio' ? 'Recording' : 'Document');
}

/** Whether a sample is a recording, by the type Arcade stored it with. */
export function isAudioSample(sample: Pick<Sample, 'content_type'>): boolean {
  return /^(audio|video)\//.test(sample.content_type);
}

export async function listSamples(secret: string): Promise<Sample[]> {
  const response = await fetch(`${arcadeOrigin()}/v1/samples`, {
    headers: { Authorization: `Bearer ${secret}` },
  });
  const text = await response.text();
  if (!response.ok) {
    throw await readError(response.status, text);
  }
  const body = JSON.parse(text) as Sample[] | { samples?: Sample[] };
  return Array.isArray(body) ? body : (body.samples ?? []);
}

export type JobSource = { file: File } | { sample: string } | { uploadId: string };

export interface JobOptions {
  /** A language tag such as `en-IE`, for a recording. */
  locale?: string;
}

/** Above this a file goes up through `POST /v1/uploads` rather than multipart. */
export const MULTIPART_MAX_BYTES = 20 * 1024 * 1024;
export const AUDIO_MAX_BYTES = 500 * 1024 * 1024;

function put(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: XMLHttpRequestBodyInit,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(method, url);
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded / event.total);
      }
    };
    request.onload = async () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(request.responseText);
      } else {
        reject(await readError(request.status, request.responseText));
      }
    };
    request.onerror = () => reject(new V1Error(0, undefined, 'The upload did not reach the API.'));
    request.send(body);
  });
}

interface UploadSlot {
  upload_id: string;
  upload_url: string;
  method: string;
  headers: Record<string, string>;
}

/** A file too large for multipart: sign a slot, PUT the bytes there, return the upload id. */
export async function uploadDirect(
  file: File,
  secret: string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const response = await fetch(`${arcadeOrigin()}/v1/uploads`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
    }),
  });
  const text = await response.text();
  if (!response.ok) {
    throw await readError(response.status, text);
  }
  const slot = JSON.parse(text) as UploadSlot;
  await put(slot.method || 'PUT', slot.upload_url, slot.headers ?? {}, file, onProgress);
  return slot.upload_id;
}

/** `POST /v1/jobs`, reporting upload progress for a file. Resolves to the job id. */
export async function submitJob(
  source: JobSource,
  secret: string,
  onProgress?: (fraction: number) => void,
  options: JobOptions = {},
): Promise<string> {
  if ('file' in source && source.file.size > MULTIPART_MAX_BYTES) {
    const uploadId = await uploadDirect(source.file, secret, onProgress);
    return submitJob({ uploadId }, secret, undefined, options);
  }
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${arcadeOrigin()}/v1/jobs`);
    request.setRequestHeader('Authorization', `Bearer ${secret}`);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded / event.total);
      }
    };
    request.onload = async () => {
      if (request.status >= 200 && request.status < 300) {
        resolve((JSON.parse(request.responseText) as { id: string }).id);
      } else {
        reject(await readError(request.status, request.responseText));
      }
    };
    request.onerror = () => reject(new V1Error(0, undefined, 'The upload did not reach the API.'));
    if ('file' in source) {
      const form = new FormData();
      form.append('file', source.file, source.file.name);
      if (options.locale) {
        form.append('locale', options.locale);
      }
      request.send(form);
    } else {
      request.setRequestHeader('Content-Type', 'application/json');
      const body = 'sample' in source ? { sample: source.sample } : { upload_id: source.uploadId };
      request.send(
        JSON.stringify({ ...body, ...(options.locale ? { locale: options.locale } : {}) }),
      );
    }
  });
}

/** What a refused submission means to the person who made it. */
export function describeSubmitError(error: unknown): string {
  if (!(error instanceof V1Error)) {
    return 'Something went wrong starting the job. Try again.';
  }
  switch (error.code) {
    case 'TOO_MANY_IN_FLIGHT':
      return 'A job is already running. The playground takes one job at a time on your account — try again when it finishes.';
    case 'INSUFFICIENT_CREDITS':
      return 'You have used this month’s credits. Samples are still free.';
    case 'UPLOAD_TOO_LARGE':
      return 'That file is over the size limit: 20 MB for a document, 500 MB for a recording.';
    case 'SAMPLE_NOT_FOUND':
      return 'That sample is no longer available.';
    case 'UPLOAD_NOT_FOUND':
      return 'The upload expired before the job started. Try again.';
    default:
      return error.message || 'Something went wrong starting the job. Try again.';
  }
}
